import React, { useState, useEffect } from 'react';
import { Error } from '@progress/kendo-react-labels';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns';
import { useIntl } from 'react-intl';
import { Input, Switch } from '@progress/kendo-react-inputs';
import loadingIcon from '../loading.svg';
import DGAUserACC from './DGAUserAcc';
import { getAuthToken, handleAuthTokenError, BASEURI } from '../utils';
import { advancedFilter } from '../utils';


function UploadJobDialog(props) {

    const [allowedGraphs, setAllowedGraphs] = useState([]);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [userAccCredentials, setUserAccCredentials] = useState({});
    const [credentialsError, setCredentialsError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [graphs, setGraphs] = useState([]);
    const intl = useIntl();

    const allowedGraphTypes = [
        'raw',
        'Raw',
        'custom',
        'Custom Formula'
    ]

    useEffect(()=>{
        setGraphs(allowedGraphs)
    }, [allowedGraphs])

    useEffect(() => {
        setAllowedGraphs(props.graphs.filter(g => allowedGraphTypes.indexOf(g.calculation) !== -1));
    }, [props.graphs]);

    function itemRender(li, itemProps) {

        const itemChildren = <div> {li.props.children} [{itemProps.dataItem.calculation}] <div style={{ color: "#00F" }}> {itemProps.dataItem.chart.name} </div> </div>;
        return React.cloneElement(li, li.props, itemChildren);
    }

    function getGraph(id) {
        return graphs.find(g => g.id === id);
    }

    function validateForm() {
        let errors = {};
        let isValid = true;

        // Format: OB-XXXX-X... (e.g., OB-1301-1163, OB-0602-35)
        const regionCodeRegex = /^OB-\d{4}-\d+$/;

        if (!props.job.region_code || !regionCodeRegex.test(props.job.region_code)) {
            isValid = false;
            errors.region_code = intl.formatMessage({ 
                id: 'app.dga.uploadJobDialog.invalidRegionCode', 
                defaultMessage: 'Invalid Region Code format. Expected format like OB-1301-1163' 
            });
        }

        if (!userAccCredentials || !userAccCredentials.username || !userAccCredentials.password) {
            isValid = false;
            errors.userAccCredentials = intl.formatMessage({
                id: 'app.dga.uploadJobDialog.invalidCredentials',
                defaultMessage: 'DGA Account Credentials are required.'
            });
        }

        // Add other field validations here

        setValidationErrors(errors);
        return isValid;
    }

    function saveCredential(jobId, create) {
        setCredentialsError('');
        let url = `${BASEURI}/api/dga/credentials/${create ? 'create' : 'update'}/${jobId}/`,
            options = {
                method: create ? 'POST' :'PUT',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
                body: JSON.stringify(userAccCredentials)
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => {
                        if (response.status !== 200 && response.status !== 201)
                            return response.text().then(text => { throw new Error(text) });

                        return response.json();
                    })
                    .then((credential) => {
                        setUserAccCredentials(credential)
                        setCredentialsError('')
                    })
                    .catch(e => {
                        console.error(e)
                        setCredentialsError(e.message || 'Could not save DGA credentials')
                    });

            });
    }

    const handleGraphFilterChange =  (e) => {
        const value = e.filter.value;
        advancedFilter(
            value,
            allowedGraphs,
            (filteredGraphs)=> setGraphs(filteredGraphs),
            ['label', 'calculation', 'name', 'y_axis_legend']
        )
    }


    return (
        <Dialog
            title={props.job.id
                ? intl.formatMessage({ id: 'app.dga.uploadJobDialog.titleEdit', defaultMessage: 'Edit' })
                : intl.formatMessage({ id: 'app.dga.uploadJobDialog.titleAdd', defaultMessage: 'Add New' })}
            onClose={props.toggle}
        >

            <div className='modal-wrap text-start'>
                <div className='modal-form-wrap'>
                    <div className='alert-modal-form-inputs-wrap'>

                        <div className='modal-form-row'>
                            <div>
                                <Input
                                    required={true}
                                    name='region_code'
                                    label={intl.formatMessage({ id: 'app.dga.uploadJobDialog.regionCode', defaultMessage: 'REGION CODE' })}
                                    value={props.job.region_code}
                                    onChange={props.onChange}
                                    valid={!validationErrors.region_code}
                                />
                                {validationErrors.region_code && <Error>{validationErrors.region_code}</Error>}
                            </div>
                        </div>
                        <div className='modal-form-row'>
                            <DropDownList
                                required={true}
                                label={intl.formatMessage({ id: 'app.dga.uploadJobDialog.extractionChart', defaultMessage: 'Extraction Chart m³' })}
                                name='totalizer'
                                data={graphs}
                                textField='label'
                                dataItemKey='id'
                                value={getGraph(props.job.totalizer)}
                                itemRender={itemRender}
                                onChange={props.onChange}
                                onFilterChange={handleGraphFilterChange}
                                filterable={true}
                            />
                            <Input
                                name='totalizer_initial_value'
                                label={intl.formatMessage({ id: 'app.dga.uploadJobDialog.totalizerInitValue', defaultMessage: 'TOTALIZER INITIAL VALUE' })}
                                value={props.job.totalizer_initial_value}
                                onChange={props.onChange}
                            />
                        </div>
                        <div className='modal-form-row'>
                            <DropDownList
                                required={true}
                                label={intl.formatMessage({ id: 'app.dga.uploadJobDialog.flowGraph', defaultMessage: 'FLOW GRAPH' })}
                                name='flow'
                                data={graphs}
                                textField='label'
                                dataItemKey='id'
                                value={getGraph(props.job.flow)}
                                itemRender={itemRender}
                                onChange={props.onChange}
                                onFilterChange={handleGraphFilterChange}
                                filterable={true}
                            />
                        </div>
                        <div className='modal-form-row'>
                            <DropDownList
                                required={true}
                                label={intl.formatMessage({ id: 'app.dga.uploadJobDialog.wellLevelGraph', defaultMessage: 'WELL LEVEL GRAPH' })}
                                name='well_water_level'
                                data={graphs}
                                textField='label'
                                dataItemKey='id'
                                value={getGraph(props.job.well_water_level)}
                                itemRender={itemRender}
                                onChange={props.onChange}
                                onFilterChange={handleGraphFilterChange}
                                filterable={true}
                            />
                        </div>
                        <div className='modal-form-row'>
                            <label className='k-form-field' style={{ marginTop: '20px' }}>
                                <span>
                                    {intl.formatMessage({ id: 'app.dga.uploadJobDialog.descriptionNote', defaultMessage: 'DESCRIPTION / NOTES' })}
                                </span>
                                <textarea
                                    className='border-b-2 border-b-gray-300 bg-gray-100 rounded-md'
                                    name='description'
                                    style={{ width: '100%', textAlign: 'start' }}
                                    value={props.job.description}
                                    onChange={props.onChange}
                                >
                                </textarea>
                            </label>
                        </div>
                        <div className='modal-form-row'>
                            <div className="ag-switch-container mt-5 flex flex-wrap items-center gap-5 text-gray-500">
                                <span className="text-start min-w-0">
                                    {intl.formatMessage({ id: 'app.dga.uploadJobDialog.enabled', defaultMessage: 'Enabled' })}
                                </span>
                                <Switch
                                    name='enabled'
                                    checked={props.job.enabled}
                                    onChange={props.onChange}
                                />
                            </div>
                        </div>
                        <fieldset>
                            <legend>{intl.formatMessage({ id: 'app.dga.jobTable.dgaAcc', defaultMessage: 'DGA Account Credentials' })}</legend>
                            <DGAUserACC
                                jobId={props?.job?.id}
                                userAccCredentials={userAccCredentials}
                                setUserAccCredentials={setUserAccCredentials}
                            />
                            {validationErrors.userAccCredentials && <Error>{validationErrors.userAccCredentials}</Error>}
                            {credentialsError && <Error>{credentialsError}</Error>}
                        </fieldset>
                    </div>
                    {/* error text */}
                    <div className="text-start" style={{ color: 'red', marginTop: '20px' }}>
                        {props.error}
                    </div>
                </div>
            </div>
            <DialogActionsBar>
                {props.job.id &&
                    <button className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md delete" onClick={props.delete}>
                        {props.deleting ? <img style={{ color: 'gray', width: '25px' }} src={loadingIcon} alt='loading'></img> :
                            intl.formatMessage({ id: 'app.dga.uploadJobDialog.delete', defaultMessage: 'DELETE' })}
                    </button>
                }
                <button className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md" onClick={props.toggle}>
                    {intl.formatMessage({ id: 'app.dga.uploadJobDialog.done', defaultMessage: 'DONE' })}
                </button>
                <button className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md" onClick={() => {
                    if (validateForm()) {
                        props.save((jobId) => {
                            saveCredential(jobId, props.job.id ? false : true);
                        });
                    }
                }}>
                    {props.saving ? <img style={{ color: 'gray', width: '25px' }} src={loadingIcon} alt='loading'></img> : props.job.id ?
                        intl.formatMessage({ id: 'app.dga.uploadJobDialog.update', defaultMessage: 'Update' }) :
                        intl.formatMessage({ id: 'app.dga.uploadJobDialog.add', defaultMessage: 'Add' })}
                </button>
            </DialogActionsBar>
        </Dialog>
        );
}


export default UploadJobDialog;